import { displayText, clientUserText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import api, { getCsrfToken } from '../../../../services/api';
import { validateField, validateForm } from '../../../../Utils/SessionsValidationsSchema';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import AppConfig from '../../../../../config/applicationconfig';

const AddCustomSession = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
const { username, id, sessionId } = useParams();
const navigate = useNavigate();
const location = useLocation();

// IMPORTANT:
// add-custom/:sessionId  => load saved custom type template
// edit-custom/:sessionId => edit existing custom session
const isEditMode = location.pathname.includes('/edit-custom/');
const isTemplateMode = location.pathname.includes('/add-custom/') && !!sessionId;

  const generatePasscode = () => {
    return Math.floor(10000 + Math.random() * 90000).toString();
  };

  const [formData, setFormData] = useState({
  witness: '',
  volume: '',
  introducing_party: [],
  date: '',
  court_reporting_firm: '',
  location: '',
  session_passcode: generatePasscode(),
  details: '',
  schedule_video: false,

  // Zoom scheduling fields
  zoom_tech: '',
  registrants: '',
  meeting_duration: '60',
  meeting_time: '',
  meeting_timezone: '',

  custom_type: '',

  // Zoom meeting fields
  zoom_meeting_id: '',
  zoom_join_url: '',
  zoom_start_url: '',
  zoom_password: '',
  zoom_created_at: '',
});

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [customSessionData, setCustomSessionData] = useState(null);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingResellers, setLoadingResellers] = useState(false);

  // Load Case + Custom Session Placeholder
  useEffect(() => {
    const initialize = async () => {
      await getCsrfToken();

      try {
        const caseResponse = await api.get(`/cases/${id}`);
        setCaseData(caseResponse.data.data);
      } catch (err) {
        toast.error(displayText('Failed to load case details'));
        navigate(`/${username}/dashboard/cases`);
      }

      if (sessionId) {
  try {
    const response = await api.get(`/cases/${id}/custom-sessions`);
    const customSessions = Array.isArray(response.data)
      ? response.data
      : response.data.data || [];

    const found = customSessions.find((s) => s.id === parseInt(sessionId));

    if (found) {
      setCustomSessionData(found);

      let parsedIntroducingParty = [];
      try {
        if (!found.introducing_party) {
          parsedIntroducingParty = [];
        } else if (Array.isArray(found.introducing_party)) {
          parsedIntroducingParty = found.introducing_party;
        } else {
          parsedIntroducingParty = JSON.parse(found.introducing_party);
        }
      } catch {
        parsedIntroducingParty = [];
      }

      setFormData({
        witness: isTemplateMode ? '' : (found.witness || found.name || ''),
        volume: isTemplateMode ? '' : (found.volume || ''),
        custom_type: found.custom_type || '',
        date: isTemplateMode ? '' : (found.date ? found.date.split('T')[0] : ''),
        court_reporting_firm: isTemplateMode ? '' : (found.court_reporting_firm || ''),
        location: isTemplateMode ? '' : (found.location || ''),
        session_passcode: generatePasscode(),
        details: isTemplateMode ? '' : (found.details || ''),
        introducing_party: isTemplateMode ? [] : parsedIntroducingParty,
        schedule_video: isTemplateMode ? false : !!found.schedule_video,

        zoom_tech: isTemplateMode ? '' : (found.zoom_tech || (found.schedule_video ? 'zoom' : '')),
        registrants: isTemplateMode ? '' : (found.registrants || ''),
        meeting_duration: isTemplateMode ? '60' : (found.meeting_duration || '60'),
        meeting_time: isTemplateMode ? '' : (found.meeting_time || ''),
        meeting_timezone: isTemplateMode ? '' : (found.meeting_timezone || ''),

        zoom_meeting_id: isTemplateMode ? '' : (found.zoom_meeting_id || ''),
        zoom_join_url: isTemplateMode ? '' : (found.zoom_join_url || ''),
        zoom_start_url: isTemplateMode ? '' : (found.zoom_start_url || ''),
        zoom_password: isTemplateMode ? '' : (found.zoom_password || found.session_passcode || ''),
        zoom_created_at: isTemplateMode ? '' : (found.zoom_created_at || ''),
      });
    } else {
      toast.error(displayText('Custom session type not found'));
      navigate(`/${username}/dashboard/case/${id}`);
    }
  } catch (err) {
    console.error(err);
    toast.error(displayText('Failed to load custom session type'));
    navigate(`/${username}/dashboard/case/${id}`);
  }
}
    };

    initialize();
  }, [id, sessionId, username, navigate]);

  // Fetch Users
  useEffect(() => {
    const fetchCombinedUsers = async () => {
      setLoadingUsers(true);
      try {
        const token = localStorage.getItem('sanctum_token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/webmanager/client-users/combined-for-introducing`,
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
        if (response.data.success) setCombinedUsers(response.data.data || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchCombinedUsers();
  }, []);

  // Fetch Resellers
  useEffect(() => {
    const fetchResellers = async () => {
      setLoadingResellers(true);
      try {
        const response = await api.get('/resellers');
        setResellers(response.data.data || response.data || []);
      } catch (error) {
        console.error('Error fetching resellers:', error);
      } finally {
        setLoadingResellers(false);
      }
    };
    fetchResellers();
  }, []);

  const handleInputChange = (e) => {
  const { name, value, type, checked } = e.target || {};

  if (name === 'schedule_video') {
    const isEnabled = checked;

    setFormData((prev) => ({
      ...prev,
      schedule_video: isEnabled,
      zoom_tech: isEnabled ? 'zoom' : '',
      registrants: isEnabled ? prev.registrants : '',
      meeting_duration: isEnabled ? (prev.meeting_duration || '60') : '',
      meeting_time: isEnabled ? prev.meeting_time : '',
      meeting_timezone: isEnabled ? prev.meeting_timezone : '',

      // Zoom meeting fields
      zoom_meeting_id: isEnabled ? prev.zoom_meeting_id : '',
      zoom_join_url: isEnabled ? prev.zoom_join_url : '',
      zoom_start_url: isEnabled ? prev.zoom_start_url : '',
      zoom_password: isEnabled ? (prev.session_passcode || '') : '',
      zoom_created_at: isEnabled ? prev.zoom_created_at : '',
    }));

    setErrors((prev) => ({
      ...prev,
      zoom_tech: '',
      registrants: '',
      meeting_duration: '',
      meeting_time: '',
      meeting_timezone: '',
    }));

    return;
  }

  setFormData((prev) => ({
    ...prev,
    [name]: type === 'checkbox' ? checked : value,
  }));

  if (errors[name]) {
    setErrors((prev) => ({ ...prev, [name]: '' }));
  }
};
  const handleBlur = async (e) => {
    const { name } = e.target;
    await validateField(name, formData, setErrors, 'custom');
  };

  const handleIntroducingPartySelect = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue && !formData.introducing_party.includes(selectedValue)) {
      setFormData(prev => ({
        ...prev,
        introducing_party: [...prev.introducing_party, selectedValue],
      }));
      e.target.value = '';
    }
  };

  const removeIntroducingParty = (partyToRemove) => {
    setFormData(prev => ({
      ...prev,
      introducing_party: prev.introducing_party.filter(p => p !== partyToRemove),
    }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (isSubmitting) return;

  setIsSubmitting(true);
  setIsLoading(true);

  if (formData.introducing_party.length === 0) {
    toast.error('Please select at least one introducing party');
    setIsLoading(false);
    setIsSubmitting(false);
    return;
  }

  try {
    const isValid = await validateForm(formData, setErrors, 'custom');
    if (!isValid) {
      toast.error('Please fill all required fields correctly.');
      return;
    }

    const payload = {
  case_id: parseInt(id),
  custom_type: formData.custom_type,
  witness: formData.witness,
  volume: formData.volume || null,
  introducing_party: formData.introducing_party,
  date: formData.date,
  court_reporting_firm: formData.court_reporting_firm,
  location: formData.location,
  session_passcode: formData.session_passcode,
  details: formData.details || null,

  schedule_video: !!formData.schedule_video,
  zoom_tech: formData.schedule_video ? 'zoom' : null,
  registrants: formData.schedule_video ? formData.registrants : null,
  meeting_duration: formData.schedule_video ? formData.meeting_duration : null,
  meeting_time: formData.schedule_video ? formData.meeting_time : null,
  meeting_timezone: formData.schedule_video ? formData.meeting_timezone : null,

  session_type: 'custom',
  status: 'pending',
};

    let response;

if (isEditMode || isTemplateMode) {
  response = await api.put(`/cases/${id}/custom-sessions/${sessionId}`, payload);
  toast.success(
    isEditMode
      ? displayText('Custom session updated successfully!')
      : displayText('Custom session created successfully!')
  );
} else {
  response = await api.post(`/cases/${id}/custom-sessions`, payload);
  toast.success(displayText('Custom session created successfully!'));
}

    setTimeout(() => {
      navigate(`/${username}/dashboard/case/${id}`);
    }, 1800);

  } catch (error) {
    console.error(error);
    const errorMsg = error.response?.data?.message || 
      (isEditMode ? 'Failed to update custom session' : 'Failed to create custom session');
    toast.error(errorMsg, { /* ... */ });
  } finally {
    setIsLoading(false);
    setIsSubmitting(false);
  }
};

  const handleCancel = () => {
    if (isSubmitting) return;
    navigate(`/${username}/dashboard/case/${id}`);
  };

  if (!caseData || ((isEditMode || isTemplateMode) && !customSessionData)) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem', color: theme.colors.textPrimary }}>{displayText(" Loading custom session... ")}</div>
  );
}

  return (
    <>
      {/* Breadcrumb */}
      <div style={{ padding: '0rem 1.0rem 1.0rem 0rem', margin: 0 }}>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0" style={{ fontSize: '10px', padding: 0, background: 'transparent' }}>
            <li className="breadcrumb-item">
              <Link to={`/${username}/dashboard/cases`} style={{ color: theme.colors.primary, textDecoration: 'none', fontWeight: 'bold' }}>
                {AppConfig.casename} Management
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/${username}/dashboard/case/${id}`} style={{ color: theme.colors.textPrimary, textDecoration: 'none', fontWeight: 'bold' }}>
                {caseData.case_name}
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: theme.colors.textPrimary, fontWeight: 'bold' }}>
              Add: {customSessionData.custom_type}
            </li>
          </ol>
        </nav>
      </div>

      {/* Main Container */}
      <div className="container-fluid py-4" style={{
        backgroundColor: theme.colors.bgPrimary,
        color: theme.colors.textPrimary,
        fontFamily: theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif',
        fontSize: 'clamp(10px, 2.5vw, 11px)',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${theme.colors.borderColor || '#ddd'}`,
        margin: '0 1.0rem 1.0rem 0',
      }}>
        <ToastContainer position="top-right" autoClose={3000} theme="light" />

        <div style={{ borderRadius: '10px', border: `1px solid ${theme.colors.borderColor}`, overflow: 'hidden', backgroundColor: theme.colors.bgPrimary }}>
          <div style={{
            background: theme.gradients.primary,
            color: theme.colors.textWhite,
            padding: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            gap: '12px',
          }}>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              Add: {customSessionData.custom_type}
            </h5>
            <h5 style={{ fontSize: '12px', fontWeight: '600', margin: 0 }}>
              Related To: {caseData.case_name}
            </h5>
          </div>

          <div style={{ padding: '16px' }}>
            <form id="addCustomSessionForm" onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: window.innerWidth < 600 ? 'column' : 'row', gap: '24px', alignItems: 'flex-start' }}>

                {/* Left Column */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
                      {customSessionData.custom_type} Name *
                    </label>
                    <input
                      type="text"
                      id="witness"
                      name="witness"
                      value={formData.witness}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.witness ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.witness ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      }}
                    />
                    {errors.witness && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.witness}</div>}
                  </div>

                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Volume</label>
                    <input
                      type="text"
                      name="volume"
                      value={formData.volume}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`form-control ${errors.volume ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.volume ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                      }}
                    />
                  </div>

                  {/* Introducing Party */}
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>{clientUserText(" Introducing Party* (Client Admins & Users) ")}</label>
                    <select
                      onChange={handleIntroducingPartySelect}
                      disabled={isSubmitting || loadingUsers}
                      className={`form-control ${errors.introducing_party ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.introducing_party ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        outline: 'none',
                      }}
                    >
                      <option value="">{loadingUsers ? 'Loading...' : 'Select to add...'}</option>
                      {combinedUsers.map((user) => (
                        <option key={`${user.type}-${user.id}`} value={user.display_name}>
                          {user.display_name}
                        </option>
                      ))}
                    </select>

                    {formData.introducing_party.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {formData.introducing_party.map((party, index) => (
                          <div key={index} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.textWhite,
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '9px',
                            fontWeight: '500',
                          }}>
                            <span style={{ marginRight: '6px' }}>{party}</span>
                            <button type="button" onClick={() => removeIntroducingParty(party)} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Date*</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.date ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: errors.date ? '6px 32px 6px 10px' : '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                      }}
                    />
                    {errors.date && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.date}</div>}
                  </div>

                  {/* Court Reporting Firm */}
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Reporting Lead Name*</label>
                    <select
                      name="court_reporting_firm"
                      value={formData.court_reporting_firm}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting || loadingResellers}
                      className={`form-control ${errors.court_reporting_firm ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.court_reporting_firm ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                      }}
                    >
                      <option value="">{loadingResellers ? 'Loading firms...' : 'Select Firm'}</option>
                      {resellers.map((reseller) => {
                        const displayName = reseller.company_name || reseller.name || `${reseller.first_name || ''} ${reseller.last_name || ''}`.trim() || 'Unknown Firm';
                        return <option key={reseller.id} value={displayName}>{displayName}</option>;
                      })}
                    </select>
                    {errors.court_reporting_firm && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.court_reporting_firm}</div>}
                  </div>

                  {/* Location */}
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Location* (City)</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.location ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                      }}
                    />
                    {errors.location && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.location}</div>}
                  </div>

                  {/* Session Passcode */}
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>{displayText("Session Passcode* (Auto-Generated)")}</label>
                    <input
                      type="text"
                      name="session_passcode"
                      value={formData.session_passcode}
                      readOnly
                      disabled={isSubmitting}
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        cursor: 'not-allowed',
                      }}
                    />
                  </div>

                  {/* Details */}
                  <div>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Details</label>
                    <textarea
                      name="details"
                      value={formData.details}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      rows="2"
                      disabled={isSubmitting}
                      className={`form-control ${errors.details ? 'is-invalid' : ''}`}
                      style={{
                        width: '100%',
                        border: `1px solid ${errors.details ? theme.colors.danger : theme.colors.borderColor}`,
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '10px',
                        backgroundColor: theme.colors.bgPrimary,
                        color: '#666',
                        resize: 'vertical',
                      }}
                    />
                    {errors.details && <div className="invalid-feedback" style={{ color: theme.colors.danger, fontSize: '9px' }}>{errors.details}</div>}
                  </div>
                </div>

                {/* Right Column - Video Scheduling */}
                <div style={{
                  width: window.innerWidth < 600 ? '100%' : '1px',
                  height: window.innerWidth < 600 ? '1px' : 'auto',
                  backgroundImage: window.innerWidth < 600
                    ? `repeating-linear-gradient(to right, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`
                    : `repeating-linear-gradient(to bottom, ${theme.colors.borderColor} 0px, ${theme.colors.borderColor} 4px, transparent 4px, transparent 8px)`,
                  margin: window.innerWidth < 600 ? '12px 0' : '0 16px',
                  minHeight: window.innerWidth >= 600 ? '400px' : '1px',
                }} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', margin: 0 }}>Schedule a Video Conference?</label>
                    <div
                      style={{
                        position: 'relative',
                        width: '50px',
                        height: '24px',
                        borderRadius: '12px',
                        backgroundColor: formData.schedule_video ? theme.colors.success : '#ccc',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => {
  if (!isSubmitting) {
    handleInputChange({
      target: {
        name: 'schedule_video',
        checked: !formData.schedule_video,
      },
    });
  }
}}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '2px',
                        left: formData.schedule_video ? '28px' : '2px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.textWhite,
                        transition: 'left 0.3s ease',
                      }} />
                    </div>
                    <input
  type="checkbox"
  id="scheduleVideo"
  name="schedule_video"
  checked={formData.schedule_video}
  readOnly
  style={{ display: 'none' }}
/>
                  </div>

                  {formData.schedule_video && (
                    <>
                    <div>
  <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
    Zoom Meeting ID
  </label>
  <input
    type="text"
    name="zoom_meeting_id"
    value={formData.zoom_meeting_id}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
    Zoom Join URL
  </label>
  <input
    type="text"
    name="zoom_join_url"
    value={formData.zoom_join_url}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
    Zoom Start URL
  </label>
  <input
    type="text"
    name="zoom_start_url"
    value={formData.zoom_start_url}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be generated after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
    Zoom Password
  </label>
  <input
    type="text"
    name="zoom_password"
    value={formData.zoom_password}
    readOnly
    disabled
    className="form-control"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      cursor: 'not-allowed',
    }}
  />
</div>

<div>
  <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
    Zoom Created At
  </label>
  <input
    type="text"
    name="zoom_created_at"
    value={formData.zoom_created_at}
    readOnly
    disabled
    className="form-control"
    placeholder="Will be filled after Zoom meeting is created"
    style={{
      width: '100%',
      border: `1px solid ${theme.colors.borderColor}`,
      borderRadius: '4px',
      padding: '6px 10px',
      fontSize: '10px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      cursor: 'not-allowed',
    }}
  />
</div>
                      <div>
                        <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Registrants</label>
                        <input type="text" name="registrants" value={formData.registrants} onChange={handleInputChange} className="form-control" style={{ fontSize: '10px' }} />
                      </div>

                      <div>
                        <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Meeting Duration*</label>
                        <input type="text" name="meeting_duration" value={formData.meeting_duration} onChange={handleInputChange} onBlur={handleBlur} required className={`form-control ${errors.meeting_duration ? 'is-invalid' : ''}`} style={{ fontSize: '10px' }} />
                      </div>

                      <div>
                        <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Meeting Time*</label>
                        <input type="time" name="meeting_time" value={formData.meeting_time} onChange={handleInputChange} onBlur={handleBlur} required className={`form-control ${errors.meeting_time ? 'is-invalid' : ''}`} style={{ fontSize: '10px' }} />
                      </div>

                      <div>
                        <label style={{ color: theme.colors.textPrimary, fontSize: '10px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>Meeting Timezone*</label>
                        <select name="meeting_timezone" value={formData.meeting_timezone} onChange={handleInputChange} onBlur={handleBlur} required className={`form-control ${errors.meeting_timezone ? 'is-invalid' : ''}`} style={{ fontSize: '10px' }}>
                          <option value="">Select Timezone</option>
                          <option value="Asia/Kolkata">India Standard Time</option>
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: window.innerWidth < 600 ? 'column' : 'row', justifyContent: 'space-between', padding: '12px', backgroundColor: theme.colors.bgPrimary }}>
          <div style={{ display: 'flex', gap: '8px', flexDirection: window.innerWidth < 600 ? 'column' : 'row' }}>
            <button onClick={handleCancel} disabled={isSubmitting} style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, fontSize: '9px', fontWeight: '600', padding: '8px 12px', borderRadius: '8px', border: 'none' }}>
              Cancel
            </button>
            <button type="submit" form="addCustomSessionForm" disabled={isLoading} style={{ background: theme.gradients.primary, color: theme.colors.textWhite, fontSize: '9px', fontWeight: '600', padding: '8px 12px', borderRadius: '8px', border: 'none' }}>
              {isLoading ? 'Saving...' : displayText('Save Custom Session')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddCustomSession;